import type { LensKind } from '../lib/lens'
import { useI18n, segments } from '../lib/i18n'

/**
 * Lens picker (v0.21.0, D-124) — choose what to look at, not what to store.
 * "Everyone" stays the default so nobody loses sight of the archive; the focused
 * lenses exist for the moment a line grows past what one screen can hold.
 */
const ORDER: LensKind[] = ['everyone', 'close', 'extended', 'ancestors', 'descendants', 'hourglass', 'paternal', 'maternal']

export default function LensBar({ kind, onKind, collaterals, onCollaterals, focusName, shown, total }: {
  kind: LensKind
  onKind: (k: LensKind) => void
  collaterals: boolean
  onCollaterals: (v: boolean) => void
  focusName?: string
  shown: number
  total: number
}) {
  const { t } = useI18n()
  const focused = kind !== 'everyone'
  return (
    <div className="mb-3">
      {/* The row had no name anywhere a user could see it — the refusal copy that
          points people here could not honestly call it anything (D-144). */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-600 pe-1.5 flex-shrink-0" title="Show one branch of the family at a time">Views</span>
        {ORDER.map(k => (
          <button key={k} onClick={() => onKind(k)}
            className={`px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap flex-shrink-0 transition-colors ${
              kind === k ? 'bg-white/10 text-gold-300 border border-gold-500/30'
                         : 'text-slate-500 hover:text-slate-300 hover:bg-white/5 border border-transparent'}`}>
            {t(`lens.${k}`)}
          </button>
        ))}
      </div>
      {focused && !focusName && (
        <p className="text-[11px] text-amber-300/80 mt-1.5">
          {t('lens.hint')}
        </p>
      )}
      {focused && focusName && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-500">
          <span>
            {segments(t('lens.showing', { shown, total })).map((sg, i) =>
              sg.tag === 'b' ? <b key={i} className="text-slate-300">{sg.text}</b> : <span key={i}>{sg.text}</span>)}
            {focusName ? <> · {segments(t('lens.centredOn', { name: focusName })).map((sg, i) =>
              sg.tag === 'b' ? <b key={i} className="text-slate-300">{sg.text}</b> : <span key={i}>{sg.text}</span>)}</> : null}
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer hover:text-slate-300">
            <input type="checkbox" checked={collaterals} onChange={e => onCollaterals(e.target.checked)} className="accent-gold-500" />
            {t('lens.collaterals')}
          </label>
        </div>
      )}
    </div>
  )
}
